import { Routes } from '@angular/router';
import { ForgotPasswordComponent } from './core/auth/forgot-password/forgot-password.component';
import { LoginComponent } from './core/auth/login.component';
import { SignupComponent } from './core/auth/signup/signup.component';
import { MARKETING_ROUTES } from './home/marketing.routes';
import {
  platformRoleActivateGuard,
  platformRoleMatchGuard,
  tenantRoleActivateGuard,
  tenantRoleMatchGuard,
} from './core/guards/role.guard';
import { tenantOnboardingGuard } from './core/guards/tenant-onboarding.guard';
import { tenantWorkspaceLockGuard } from './core/guards/tenant-workspace-lock.guard';
import { tenantInactiveGuard } from './core/guards/tenant-inactive.guard';

const tenantRoutes = (): Routes => [
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./tenant-features/onboarding/onboarding-flow.component').then(
        (m) => m.OnboardingFlowComponent,
      ),
  },
  {
    path: 'payment/complete',
    loadComponent: () =>
      import('./tenant-features/billing/payment-complete.component').then(
        (m) => m.PaymentCompleteComponent,
      ),
    data: { title: 'Payment' },
  },
  {
    path: 'billing',
    loadChildren: () =>
      import('./tenant-features/billing/billing.routes').then((m) => m.BILLING_ROUTES),
    data: { title: 'Billing' },
  },
  {
    path: 'finance',
    loadChildren: () =>
      import('./tenant-features/finance/finance.routes').then((m) => m.FINANCE_ROUTES),
    data: { title: 'Finance' },
  },
  {
    path: 'settings/invoice-audit',
    pathMatch: 'full',
    redirectTo: 'finance/invoices',
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./tenant-features/home/home.routes').then((m) => m.HOME_ROUTES),
  },
  {
    path: 'orders',
    loadChildren: () => import('./tenant-features/orders/orders.routes').then((m) => m.ORDERS_ROUTES),
  },
  {
    path: 'products',
    loadChildren: () => import('./tenant-features/products/products.routes').then((m) => m.PRODUCTS_ROUTES),
  },
  {
    path: 'discounts',
    loadChildren: () => import('./tenant-features/discounts/discounts.routes').then((m) => m.DISCOUNTS_ROUTES),
  },
  {
    path: 'support-tickets',
    loadChildren: () =>
      import('./tenant-features/support-tickets/support-tickets.routes').then(
        (m) => m.SUPPORT_TICKETS_ROUTES,
      ),
  },
  {
    path: 'reviews',
    loadChildren: () =>
      import('./tenant-features/reviews/reviews.routes').then(
        (m) => m.REVIEWS_ROUTES,
      ),
  },
  {
    path: 'widgets',
    loadChildren: () =>
      import('./tenant-features/widgets/widgets.routes').then(
        (m) => m.WIDGETS_ROUTES,
      ),
  },
  {
    path: 'customers',
    loadChildren: () => import('./tenant-features/customers/customers.routes').then((m) => m.CUSTOMERS_ROUTES),
  },
  {
    path: 'analytics',
    loadChildren: () => import('./tenant-features/analytics/analytics.routes').then((m) => m.ANALYTICS_ROUTES),
  },
  {
    path: 'user-management',
    loadChildren: () =>
      import('./tenant-features/user-management/user-management.routes').then(
        (m) => m.USER_MANAGEMENT_ROUTES,
      ),
  },
  {
    path: 'settings',
    loadChildren: () => import('./tenant-features/settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
  },
  {
    path: 'policies',
    loadChildren: () =>
      import('./tenant-features/policies/policies.routes').then((m) => m.POLICIES_ROUTES),
  },
];

const platformRoutes = (): Routes => [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./platform-features/dashboard/dashboard-page.component').then(
        (m) => m.DashboardPageComponent,
      ),
  },
  {
    path: 'tenants',
    loadComponent: () =>
      import('./platform-features/tenants/tenants-page.component').then(
        (m) => m.TenantsPageComponent,
      ),
  },
  {
    path: 'tenants/:id',
    loadComponent: () =>
      import('./platform-features/tenants/tenant-info.component').then(
        (m) => m.TenantInfoComponent,
      ),
  },
  {
    path: 'billing',
    loadChildren: () =>
      import('./platform-features/billing/billing.module').then((m) => m.BillingModule),
  },
  {
    path: 'platform-users',
    loadComponent: () =>
      import('./platform-features/platform-users/platform-users-page.component').then(
        (m) => m.PlatformUsersPageComponent,
      ),
  },
  {
    path: 'sales/leads',
    loadComponent: () =>
      import('./platform-features/sales/leads-page.component').then(
        (m) => m.LeadsPageComponent,
      ),
    data: { title: 'Sales Leads' },
  },
  {
    path: 'blogs',
    loadComponent: () =>
      import('./platform-features/blog/blogs-list-page.component').then(
        (m) => m.BlogsListPageComponent,
      ),
    data: { title: 'Blogs' },
  },
  {
    path: 'blogs/new',
    loadComponent: () =>
      import('./platform-features/blog/blog-editor-page.component').then(
        (m) => m.BlogEditorPageComponent,
      ),
    data: { title: 'Create blog', blogEditorMode: 'create' },
  },
  {
    path: 'blogs/:id',
    loadComponent: () =>
      import('./platform-features/blog/blog-editor-page.component').then(
        (m) => m.BlogEditorPageComponent,
      ),
    data: { title: 'Edit blog' },
  },
  {
    path: 'blog-categories',
    loadComponent: () =>
      import('./platform-features/blog/blog-categories-list-page.component').then(
        (m) => m.BlogCategoriesListPageComponent,
      ),
    data: { title: 'Blog categories' },
  },
  {
    path: 'blog-categories/:id',
    loadComponent: () =>
      import('./platform-features/blog/blog-category-editor-page.component').then(
        (m) => m.BlogCategoryEditorPageComponent,
      ),
    data: { title: 'Blog category' },
  },
  {
    path: 'features',
    loadComponent: () =>
      import('./platform-features/features/features-page.component').then(
        (m) => m.FeaturesPageComponent,
      ),
  },
  {
    path: 'ai-models',
    loadComponent: () =>
      import('./platform-features/ai-models/ai-models-page.component').then(
        (m) => m.AIModelsPageComponent,
      ),
  },
  {
    path: 'feature-costs',
    loadComponent: () =>
      import('./platform-features/feature-costs/feature-costs-page.component').then(
        (m) => m.FeatureCostsPageComponent,
      ),
    data: { title: 'Feature costs' },
  },
  {
    path: 'ai-intents',
    loadComponent: () =>
      import('./platform-features/ai-intents/ai-intents-page.component').then(
        (m) => m.AiIntentsPageComponent,
      ),
  },
  {
    path: 'ai-intents/:intentId',
    loadComponent: () =>
      import('./platform-features/ai-intents/ai-intent-detail-page.component').then(
        (m) => m.AiIntentDetailPageComponent,
      ),
  },
  {
    path: 'roles',
    loadComponent: () =>
      import('./platform-features/roles/roles-page.component').then(
        (m) => m.RolesPageComponent,
      ),
  },
  {
    path: 'logs',
    loadComponent: () =>
      import('./platform-features/logs/logs-page.component').then(
        (m) => m.LogsPageComponent,
      ),
  },
  {
    path: 'support/tickets',
    loadComponent: () =>
      import(
        './platform-features/support-tickets-platform/platform-support-tickets-page.component'
      ).then((m) => m.PlatformSupportTicketsPageComponent),
    data: { title: 'Platform support tickets' },
  },
  {
    path: 'system-logs',
    redirectTo: 'logs',
    pathMatch: 'full',
  },
  {
    path: 'task-templates',
    loadComponent: () =>
      import('./platform-features/task-templates/task-templates-page.component').then(
        (m) => m.TaskTemplatesPageComponent,
      ),
    data: { title: 'Task templates' },
  },
  {
    path: 'scheduler',
    loadComponent: () =>
      import('./platform-features/scheduler/scheduler-page.component').then(
        (m) => m.SchedulerPageComponent,
      ),
    data: { title: 'Schedulers' },
  },
  {
    path: 'system-announcements',
    loadComponent: () =>
      import('./platform-features/system-announcements/system-announcements-page.component').then(
        (m) => m.SystemAnnouncementsPageComponent,
      ),
    data: { title: 'System announcements' },
  },
  {
    path: 'settings',
    loadComponent: () =>
      import('./platform-features/settings/settings-page.component').then(
        (m) => m.SettingsPageComponent,
      ),
  },
  {
    path: 'settings/invoice-settings',
    loadComponent: () =>
      import(
        './platform-features/settings/platform-invoice-settings-page.component'
      ).then((m) => m.PlatformInvoiceSettingsPageComponent),
    data: { title: 'Invoice Settings' },
  },
  {
    path: 'settings/signup-settings',
    loadComponent: () =>
      import(
        './platform-features/settings/platform-security-settings-page.component'
      ).then((m) => m.PlatformSecuritySettingsPageComponent),
    data: { title: 'Signup Settings' },
  },
  {
    path: 'settings/company-policies/terms',
    loadComponent: () =>
      import(
        './platform-features/company-policies/company-policy-editor.component'
      ).then((m) => m.CompanyPolicyEditorComponent),
    data: { title: 'Terms & Conditions', policySlug: 'terms' },
  },
  {
    path: 'settings/company-policies/privacy',
    loadComponent: () =>
      import(
        './platform-features/company-policies/company-policy-editor.component'
      ).then((m) => m.CompanyPolicyEditorComponent),
    data: { title: 'Privacy Policy', policySlug: 'privacy' },
  },
  {
    path: 'settings/company-policies/pricing',
    loadComponent: () =>
      import(
        './platform-features/company-policies/company-policy-editor.component'
      ).then((m) => m.CompanyPolicyEditorComponent),
    data: { title: 'Pricing Policy', policySlug: 'pricing' },
  },
];

export const routes: Routes = [
  ...MARKETING_ROUTES,
  {
    path: 'landing',
    loadComponent: () =>
      import('./tenant-features/landing/landing-page.component').then(
        (m) => m.LandingPageComponent,
      ),
    data: { title: 'Landing' },
  },
  {
    path: 'login',
    component: LoginComponent,
    data: { title: 'Login' },
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    data: { title: 'Forgot password' },
  },
  {
    path: 'auth/reset',
    loadComponent: () =>
      import('./core/auth/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
    data: { title: 'Reset password' },
  },
  {
    path: 'auth/verify',
    loadComponent: () =>
      import('./core/auth/verify-signup/verify-signup.component').then(
        (m) => m.VerifySignupComponent,
      ),
    data: { title: 'Verify Signup' },
  },
  {
    path: 'tenant/select',
    loadComponent: () =>
      import('./core/auth/tenant-selection.component').then((m) => m.TenantSelectionComponent),
      data: { title: 'Select account' },
  },
  {
    path: 'signup',
    component: SignupComponent,
    data: { title: 'Signup' },
  },
  {
    path: 'invite/:token',
    loadComponent: () =>
      import('./core/auth/invite-page/invite-page.component').then((m) => m.InvitePageComponent),
    data: { title: 'Accept invite' },
  },
  {
    path: 'shopify-connected',
    loadComponent: () =>
      import('./core/auth/shopify-connected.component').then((m) => m.ShopifyConnectedComponent),
    data: { title: 'Your Store is Connected' },
  },
  {
    path: 'custom-apps-shopify-connected',
    loadComponent: () =>
      import('./core/auth/custom-apps-shopify-connected.component').then(
        (m) => m.CustomAppsShopifyConnectedComponent,
      ),
    data: { title: 'Your Store is Connected' },
  },
  {
    path: 'platform',
    canMatch: [platformRoleMatchGuard],
    canActivate: [platformRoleActivateGuard],
    loadComponent: () =>
      import('./core/layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: platformRoutes(),
  },
  {
    path: 't/:slug',
    canMatch: [tenantRoleMatchGuard],
    canActivate: [tenantInactiveGuard, tenantRoleActivateGuard, tenantOnboardingGuard],
    loadComponent: () =>
      import('./core/layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: '',
        canActivateChild: [tenantWorkspaceLockGuard],
        children: tenantRoutes(),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];


